import { useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import { Card } from '../components/Card'
import { FragFinderHeading } from '../components/FragFinderHeading'
import { NavBar } from '../components/NavBar'
import { SearchBox } from '../components/SearchBox'
import {
  useFragrancesInfinite,
  getFragranceImageUrl,
  type IFragranceCard,
} from '../queries/useFragrances'

const PAGE_SIZE = 10

export const HomePage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [hideNavTitle, setHideNavTitle] = useState(true)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const loaderRef = useRef<HTMLDivElement | null>(null)
  const headingRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  const handleSearchClick = () => {
    searchInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // Focus after scroll animation completes
    setTimeout(() => {
      searchInputRef.current?.focus()
    }, 500)
  }

  // Handle navigation from search icon in NavBar
  useEffect(() => {
    if (location.state?.scrollToSearch) {
      window.scrollTo(0, 0)
      searchInputRef.current?.focus()
      // Clear the state to prevent re-triggering on future renders
      window.history.replaceState({}, document.title)
    }
  }, [location])

  // Infinite query for fragrances
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useFragrancesInfinite(debouncedQuery, PAGE_SIZE)

  // Flatten all pages into a single array
  const fragrances: IFragranceCard[] = data?.pages.flatMap((page) => page.items) ?? []

  // Track when the heading is scrolled past to show/hide nav title
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        setHideNavTitle(entry.isIntersecting)
      },
      { threshold: 0 },
    )

    const currentHeading = headingRef.current
    if (currentHeading) {
      observer.observe(currentHeading)
    }

    return () => {
      if (currentHeading) {
        observer.unobserve(currentHeading)
      }
    }
  }, [])

  // Debounce search query
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [searchQuery])

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0]
        if (first.isIntersecting && hasNextPage && !isFetching) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 },
    )

    const currentLoader = loaderRef.current
    if (currentLoader) {
      observer.observe(currentLoader)
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader)
      }
    }
  }, [hasNextPage, isFetching, fetchNextPage])

  const onCardClick = (slug: string) => {
    navigate(`/fragrance/${slug}`)
  }

  return (
    <>
      <NavBar
        hideTitle={hideNavTitle}
        onSearchClick={handleSearchClick}
      />
      <div
        className={
          'min-h-screen flex flex-col items-center bg-background'
        }
      >
        <div ref={headingRef}>
          <FragFinderHeading
            className={'md:text-[54px] text-[36px] mb-4 mt-[120px]'}
            text={'Explore Scents'}
          />
        </div>

        <div
          className={
            'md:text-2xl text-xl text-center mb-12 md:w-1/2 w-4/5 font-sf-pro-light'
          }
        >
          Uncover new favorites and timeless classics with our smart scent
          finder. We make discovering your next fragrance feel as good as
          wearing it.
        </div>

        <SearchBox
          ref={searchInputRef}
          className={'md:w-1/2 w-4/5 mb-6'}
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search"
        />

        {/* Image Grid */}
        <div
          className={
            'grid xl:grid-cols-5 lg:grid-cols-4 md:grid-cols-3 grid-cols-2 gap-4 p-4 mb-6'
          }
        >
          {isLoading && fragrances.length === 0 ? (
            <div className="col-span-full text-center text-gray-500">
              Loading...
            </div>
          ) : fragrances.length === 0 ? (
            <div className="col-span-full text-center text-gray-500">
              No fragrances found
            </div>
          ) : (
            fragrances.map((fragrance) => (
              <Card
                key={fragrance.id}
                slug={fragrance.slug}
                imageUrl={getFragranceImageUrl(fragrance)}
                title={fragrance.expand?.brand?.name ?? 'Unknown Brand'}
                label={fragrance.name}
                onClick={onCardClick}
              />
            ))
          )}
        </div>

        {/* Infinite scroll loader */}
        {fragrances.length > 0 && (
          <div
            ref={loaderRef}
            className="w-full py-4 text-center text-gray-500"
          >
            {isFetchingNextPage && 'Loading more...'}
            {!hasNextPage && fragrances.length > 0 && 'No more fragrances'}
          </div>
        )}
      </div>
    </>
  )
}
